import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadSharedSnapshot } from '../utils/sharing';
import { useAuth } from '../AuthContext';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// Utility functions from SumSum.jsx
const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const rounded = Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
  const parts = rounded.toString().split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (parts[1]) {
    const decimal = parts[1].replace(/0+$/, '');
    return decimal ? `${integerPart},${decimal}` : integerPart;
  }
  return integerPart;
};

const parseNumber = (str) => {
  if (!str || str.trim() === '') return 0;
  const cleaned = str.replace(/\s/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

export const SharedDocumentViewer = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [blockOpenStates, setBlockOpenStates] = useState({});

  useEffect(() => {
    loadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  const loadSnapshot = async () => {
    setLoading(true);
    try {
      const data = await loadSharedSnapshot(shareId);
      if (data) {
        setSnapshot(data);
        // Initialize all blocks as open (expanded) by default
        const initialStates = {};
        data.blocks.forEach((block, index) => {
          initialStates[index] = true;
        });
        setBlockOpenStates(initialStates);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error('Error loading shared document:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleBlock = (blockIndex) => {
    setBlockOpenStates(prev => ({
      ...prev,
      [blockIndex]: !prev[blockIndex]
    }));
  };

  const handleMakeCopy = () => {
    if (!user) {
      // Store snapshot and redirect to home where they can log in
      sessionStorage.setItem('copySnapshot', JSON.stringify(snapshot));
      navigate('/?login=true');
      toast('Please log in to save your copy', { icon: '🔐' });
      return;
    }

    // Store snapshot in sessionStorage and navigate to home
    sessionStorage.setItem('copySnapshot', JSON.stringify(snapshot));
    navigate('/');
    toast.success('Copy created! Start editing.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#0a0a0a] text-white p-4">
        <h1 className="text-2xl font-bold mb-4">Note Not Found</h1>
        <p className="text-gray-400 mb-6">This shared note doesn't exist or has been removed.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-[#7BCDBA] text-black font-semibold rounded-xl hover:bg-[#6bbdaa] transition-colors"
        >
          Go to Home
        </button>
      </div>
    );
  }

  // Calculate grand total
  const grandTotal = snapshot.blocks.reduce((total, block) => {
    return total + block.elements.reduce((sum, el) => sum + parseNumber(el.value), 0);
  }, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header with action buttons */}
      <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-[800px] mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            <span className="text-sm text-gray-400">Shared Note (Read-only)</span>
          </div>
          <button
            onClick={handleMakeCopy}
            className="px-4 py-2 bg-[#7BCDBA] text-black font-semibold rounded-lg hover:bg-[#6bbdaa] transition-colors text-sm"
          >
            {user ? 'Make a Copy' : 'Log in to Edit'}
          </button>
        </div>
      </div>

      {/* Document content - matching original editor layout */}
      <div className="flex flex-col items-center h-full w-full">
        <div className="relative w-full md:max-w-[800px] h-full flex-1">
          <div className="relative rounded-none md:rounded-[50px]">
            <div className="w-full min-h-screen bg-[#1b1e1c] px-5 pt-5 pb-24 md:p-12 md:pb-24 rounded-none md:rounded-[50px]">
              {/* Title */}
              <div className="text-[#7BCDBA] text-2xl font-bold py-2 border-b-2 border-transparent mb-4">
                {snapshot.title || 'Heading'}
              </div>

              {/* Blocks */}
              <div className="space-y-2">
                {snapshot.blocks.map((block, blockIndex) => {
                  const blockSum = block.elements.reduce((sum, el) => sum + parseNumber(el.value), 0);
                  const isOpen = blockOpenStates[blockIndex];

                  return (
                    <div key={blockIndex} className="mb-2 rounded-lg">
                      {/* Block header */}
                      <div className="flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-white/5 cursor-pointer group">
                        {/* Toggle Arrow */}
                        <button
                          onClick={() => toggleBlock(blockIndex)}
                          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                        >
                          {isOpen ? (
                            <ChevronDownIcon className="w-5 h-5" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5" />
                          )}
                        </button>
                        <div className="flex-1 text-white font-bold">
                          {block.title || 'Toggle'}
                        </div>
                        <span className="text-[#BD93D8] w-28 text-right font-bold">
                          {formatNumber(blockSum)}
                        </span>
                      </div>

                      {/* Elements - only show when open */}
                      {isOpen && (
                        <div className="ml-8 pl-2 border-l border-gray-700/50 mt-1 mb-2">
                          {block.elements.map((element, elIndex) => (
                            <div key={elIndex} className="flex items-center gap-[32px] px-2 py-1">
                              <div className="flex-1 text-white">
                                {element.name || 'Description...'}
                              </div>
                              <div className="w-28 text-right text-[#9799CA]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                {element.value || '0'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              {snapshot.blocks.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="flex justify-end items-center px-2 py-1">
                    <span className="text-gray-400 font-bold w-28 text-right">
                      {formatNumber(grandTotal)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
